import { mapAccess } from "../core/config.js";
import {
  generateAOIConfig,
  generateConfig,
  generateCustomFillConfig,
} from "../core/layers.js";
import { setElementVisibility } from "./dom.js";
import { createLegend } from "./legend.js";

/**
 * Batch-load layer metadata from the layers API.
 * GET layers/batch?ids=45,46,47
 * Response: { data: [layer, ...] } (same layer shape as the single-id GET).
 */
export function loadDataFromUrls(containerDivId, mapLayers, headers, layerRequests) {
  if (!layerRequests || layerRequests.length === 0) {
    return Promise.resolve([]);
  }

  setElementVisibility("#" + containerDivId + " .lds-spinner", true);

  const cachedResults = [];
  const fetchRequests = [];

  layerRequests.forEach((req) => {
    const { id, isAOI, showLegend } = req;
    if (mapLayers[id]) {
      cachedResults.push({ id, isAOI, showLegend, baseLayer: mapLayers[id].layer[0] });
    } else {
      fetchRequests.push(req);
    }
  });

  if (fetchRequests.length === 0) {
    return Promise.resolve(cachedResults);
  }

  const ids = fetchRequests.map(({ id }) => id).join(",");
  const url = mapAccess.url + "layers-batch?withPath=false&ids=" + ids;

  return axios
    .get(url, { headers })
    .then(function (response) {
      const items = response.data?.data || [];
      const fetchedResults = fetchRequests.map((req, index) => {
        const item =
          items.find((entry) => {
            const entryId = entry?.id ?? entry?.data?.id;
            return entryId != null && String(entryId) === String(req.id);
          }) ?? items[index];
        const baseLayer = item?.data ?? item ?? null;
        return {
          id: req.id,
          isAOI: req.isAOI,
          showLegend: req.showLegend,
          baseLayer,
        };
      });
      return cachedResults.concat(fetchedResults);
    })
    .catch(function (error) {
      console.error("Error loading batch layer data:", error);
      throw error;
    });
}

export function loadLayerFromData(
  containerDivId,
  data,
  map,
  mapLayers,
  layerConfig
) {
  setElementVisibility("#" + containerDivId + " .lds-spinner", true);
  const { customName } = layerConfig;
  const customFillImage = layerConfig.customFillImage && layerConfig.customFillImage[data.id];
  const reverseSegments = layerConfig.reverseSegments?.map(x => x + "");
  return new Promise((resolve, reject) => {
    let mapLayer = {};
    const { baseLayer, showLegend, id, isAOI } = data;

    if (!baseLayer || Object.keys(baseLayer).length === 0) {
      resolve(id);
      return;
    }

    mapLayer.full = baseLayer;
    let displayLegendName = (customName && customName[baseLayer.id]) || baseLayer.name;

    if (customName && customName.list && customName.list.includes(baseLayer.id)) {
      if (customName.prefix) {
        displayLegendName = customName.prefix + " " + displayLegendName;
      }
      if (customName.suffix) {
        displayLegendName = displayLegendName + " " + customName.suffix;
      }
    }

    if (!baseLayer.style) {
      mapLayer.type = "aoi";
      let newLayer;

      if (customFillImage) {
        newLayer = generateCustomFillConfig(map, baseLayer, baseLayer.settings, customFillImage);
      } else {
        newLayer = isAOI
          ? generateAOIConfig(baseLayer, baseLayer.settings)
          : generateConfig(baseLayer, null, isAOI);
      }

      const newLayerId = newLayer.id;
      mapLayers[newLayerId] = {
        id: newLayerId,
        type: isAOI ? "aoi" : "dataset",
        layer: [baseLayer],
      };

      if (showLegend) {
        if (!isAOI) {
          createLegend({
            containerDivId,
            mapLayers,
            map,
            name: displayLegendName,
            layerId: newLayerId,
            layers: newLayer?.style?.layers ?? [newLayer],
            isAOI: false,
            customFillImage,
            renderType: newLayer.type,
            reverseSegments: reverseSegments?.includes(newLayerId) ? true : false,
          });
        } else {
          createLegend({
            containerDivId,
            mapLayers,
            map,
            name: displayLegendName,
            layerId: newLayerId,
            fillStyle: {
              "fill-outline-color": AOIColor[newLayerId] || "#" + baseLayer.settings?.color,
            },
            renderType: "fill",
            isAOI: true,
          });
        }
      }

      map.addLayer(newLayer);
      mapLayer.layer = newLayer;
    } else {
      mapLayer.type = "data";
      let layer = baseLayer.style.layers[0];
      let source = baseLayer.style.sources.dataset;
      mapLayer.layer = layer;
      mapLayer.source = source;
      mapLayer.id = baseLayer.id + "";
      layer.source = baseLayer.id + "";

      if (baseLayer.style.layers.length <= 1) {
        map.addSource(baseLayer.id, source);
        map.addLayer(layer);
        mapLayers[layer.source] = { source: source, layer: layer, full: baseLayer };

        if (showLegend) {
          createLegend({
            containerDivId,
            mapLayers,
            map,
            layerId: mapLayer.id,
            name: displayLegendName,
            layers: baseLayer.style.layers,
            layer,
            reverseSegments: reverseSegments?.includes(mapLayer.id) ? true : false,
          });
        }
      } else {
        // Multiple layers
        if (baseLayer.style.layers && showLegend) {
          createLegend({
            containerDivId,
            mapLayers,
            layerId: mapLayer.id,
            map,
            name: displayLegendName,
            layers: baseLayer.style.layers,
            reverseSegments: reverseSegments?.includes(mapLayer.id) ? true : false,
          });
        }

        map.addSource(baseLayer.id, source);
        baseLayer.style.layers.forEach((x) => {
          x.source = baseLayer.id + "";
          map.addLayer(x);
          mapLayers[x.id] = { source: x.source, layer: x, full: baseLayer };
        });
        mapLayers[baseLayer.id + ""] = {
          linkedLayer: baseLayer.style.layers.map(x => x.id),
          full: baseLayer,
        };
      }
    }

    // PERFORMANCE: Don't wait for source to fully load - resolve immediately
    // This prevents blocking and allows layers to load in background
    resolve(id);
  });
}
